<template>
    <div class="min-h-screen bg-white flex">
        <!-- Left panel -->
        <div class="hidden lg:flex lg:w-1/2 bg-[#17364a] flex-col items-center justify-center p-16 relative overflow-hidden">
            <div class="absolute inset-0 opacity-5" style="background: repeating-linear-gradient(45deg, #A07830, #A07830 1px, transparent 1px, transparent 40px)"></div>
            <div class="relative z-10 flex flex-col items-center text-center">
                <img src="/images/logo.png" alt="Nobel Academy" class="h-16 w-auto brightness-0 invert mb-10" />
                <h2 class="text-3xl font-extrabold text-white leading-tight mb-4">
                    Join <span class="text-[#A07830]">Nobel Academy</span><br />and Start Learning Today.
                </h2>
                <p class="text-gray-500 text-sm leading-relaxed max-w-sm">
                    Create your account and get access to expert-led programs across a wide range of subjects and disciplines.
                </p>
            </div>
        </div>

        <!-- Right panel -->
        <div class="flex-1 flex flex-col items-center justify-center px-6 py-12 lg:px-16 overflow-y-auto">
            <div class="w-full max-w-md">
                <img src="/images/logo.png" alt="Nobel Academy" class="h-10 w-auto mb-8 lg:hidden" />

                <h1 class="text-2xl font-extrabold text-[#17364a] mb-2">Create account</h1>
                <p class="text-sm text-gray-500 mb-8">Fill in your details to get started.</p>

                <form class="flex flex-col gap-5" @submit.prevent="submitForm">
                    <div v-if="errors.general" class="px-4 py-3 border-l-2 border-[#A07830] bg-[#A07830]/5 text-sm text-[#A07830]">
                        {{ errors.general }}
                    </div>

                    <div class="grid grid-cols-2 gap-4">
                        <div>
                            <FloatLabel variant="in" class="w-full">
                                <InputText id="firstName" v-model="user.firstName" variant="filled" class="w-full" required
                                    :invalid="!!errors.firstName" type="text" @focus="errors.firstName = null" />
                                <label for="firstName">First Name</label>
                            </FloatLabel>
                            <div v-if="errors.firstName" class="mt-1 text-xs text-[#A07830]">{{ errors.firstName }}</div>
                        </div>
                        <div>
                            <FloatLabel variant="in" class="w-full">
                                <InputText id="lastName" v-model="user.lastName" variant="filled" class="w-full" required
                                    :invalid="!!errors.lastName" type="text" @focus="errors.lastName = null" />
                                <label for="lastName">Last Name</label>
                            </FloatLabel>
                            <div v-if="errors.lastName" class="mt-1 text-xs text-[#A07830]">{{ errors.lastName }}</div>
                        </div>
                    </div>

                    <div>
                        <FloatLabel variant="in" class="w-full">
                            <Select id="gender" v-model="user.gender" variant="filled" class="w-full" required
                                option-label="label" option-value="value" :options="genderOptions"
                                :highlightOnSelect="true" :invalid="!!errors.gender" @focus="errors.gender = null">
                                <template #option="{ option }">
                                    <span>{{ option?.label || 'Select Gender' }}</span>
                                </template>
                            </Select>
                            <label for="gender">Gender</label>
                        </FloatLabel>
                        <div v-if="errors.gender" class="mt-1 text-xs text-[#A07830]">{{ errors.gender }}</div>
                    </div>

                    <div class="grid grid-cols-2 gap-4">
                        <div>
                            <FloatLabel variant="in" class="w-full">
                                <InputText id="phone" v-model="user.phone" variant="filled" class="w-full" type="tel" required
                                    :invalid="!!errors.phone" @focus="errors.phone = null" />
                                <label for="phone">Phone</label>
                            </FloatLabel>
                            <div v-if="errors.phone" class="mt-1 text-xs text-[#A07830]">{{ errors.phone }}</div>
                        </div>
                        <div>
                            <FloatLabel variant="in" class="w-full">
                                <InputText id="whatsapp" v-model="user.whatsapp" variant="filled" class="w-full" type="tel" required
                                    :invalid="!!errors.whatsapp" @focus="errors.whatsapp = null" />
                                <label for="whatsapp">WhatsApp</label>
                            </FloatLabel>
                            <div v-if="errors.whatsapp" class="mt-1 text-xs text-[#A07830]">{{ errors.whatsapp }}</div>
                        </div>
                    </div>

                    <div>
                        <FloatLabel variant="in" class="w-full">
                            <Select id="country_id" v-model="user.country_id" variant="filled" class="w-full" required
                                :invalid="!!errors.country_id" @focus="errors.country_id = null" :options="countries"
                                option-label="name" option-value="id">
                                <template #option="{ option }">
                                    <span>{{ option?.name }}</span>
                                </template>
                            </Select>
                            <label for="country_id">Country</label>
                        </FloatLabel>
                        <div v-if="errors.country_id" class="mt-1 text-xs text-[#A07830]">{{ errors.country_id }}</div>
                    </div>

                    <div>
                        <FloatLabel variant="in" class="w-full">
                            <InputText id="age" v-model="user.age" variant="filled" class="w-full" type="number" required
                                :invalid="!!errors.age" @focus="errors.age = null" max="100" />
                            <label for="age">Age</label>
                        </FloatLabel>
                        <div v-if="errors.age" class="mt-1 text-xs text-[#A07830]">{{ errors.age }}</div>
                    </div>

                    <div>
                        <FloatLabel variant="in" class="w-full">
                            <InputText id="email" v-model="user.email" variant="filled" class="w-full" type="email" required
                                :invalid="!!errors.email" @focus="errors.email = null" />
                            <label for="email">Email</label>
                        </FloatLabel>
                        <div v-if="errors.email" class="mt-1 text-xs text-[#A07830]">{{ errors.email }}</div>
                    </div>

                    <div>
                        <FloatLabel variant="in" class="w-full">
                            <InputText id="password" v-model="user.password" variant="filled" class="w-full" required
                                type="password" :invalid="!!errors.password" @focus="errors.password = null" />
                            <label for="password">Password</label>
                        </FloatLabel>
                        <div v-if="errors.password" class="mt-1 text-xs text-[#A07830]">{{ errors.password }}</div>
                    </div>

                    <Button
                        class="w-full mt-2"
                        type="submit"
                        :loading="loading"
                        label="Create Account"
                        style="background: #17364a; border: none; border-radius: 0; font-weight: 600;"
                    />
                </form>

                <p class="mt-8 text-center text-sm text-gray-500">
                    Already have an account?
                    <a href="/login" class="font-semibold text-[#A07830] hover:underline ml-1">Sign in</a>
                </p>
            </div>
        </div>
    </div>
</template>

<script setup>
import InputText from 'primevue/inputtext';
import FloatLabel from 'primevue/floatlabel';
import Button from 'primevue/button';
import Select from 'primevue/select';
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import { useAuth } from '@/composables/useAuth';
import { getCountries } from '@/services/countryService';

const route = useRouter();
const user = ref({});
const errors = ref({});

const genderOptions = ref([
    { label: 'Select Gender', value: null, disabled: true },
    { label: 'Male', value: 1 },
    { label: 'Female', value: 2 },
    { label: 'Prefer not to say', value: 0 }
]);

const loading = ref(false);
const countries = ref([]);

getCountries().then(response => {
    countries.value = response.data.map(country => ({
        id: country.id,
        name: country.name,
        phone_code: country.phone_code,
    }));
    countries.value.unshift({ id: null, name: 'Select Country', phone_code: '', flag: '', disabled: true });
}).catch(() => {
    errors.value.general = 'Failed to load countries. Please try again later.';
});

const submitForm = () => {
    loading.value = true;

    useAuth()
        .register(user.value)
        .then(() => {
            route.push({ name: 'login' });
        })
        .catch((error) => {
            if (error.response?.status === 422) {
                const backendErrors = error.response.data.errors;
                for (const key in backendErrors) {
                    errors.value[key] = backendErrors[key][0];
                }
                return;
            }
            errors.value = { general: error.response?.data?.message ?? 'Registration failed. Please try again.' };
        })
        .finally(() => {
            loading.value = false;
        });
};
</script>
